import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "featured" | "default";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-lg overflow-hidden bg-foreground-900 cursor-pointer h-full relative"
      >
        <div className="relative h-full min-h-[420px]">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-50 group-hover:opacity-40 transition-opacity duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h3 className="text-white font-serif font-bold text-xl md:text-2xl mb-3 group-hover:text-primary-300 transition-colors duration-300">
              {article.title}
            </h3>
            <p className="text-white/70 text-sm line-clamp-2 mb-4">{article.excerpt}</p>
            <div className="flex items-center justify-between text-white/50 text-xs">
              <span>{article.publishDate}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount >= 10000
                  ? `${(article.readCount / 10000).toFixed(1)}万`
                  : article.readCount}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-white rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-100"
    >
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-800 font-medium whitespace-nowrap">
            {article.category}
          </span>
          {article.isExclusive && (
            <span className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
              独家
            </span>
          )}
        </div>
        <div className="w-full aspect-[4/3] rounded-md overflow-hidden mb-4 bg-background-200">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <h3 className="font-medium text-foreground-950 text-base mb-2 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
          {article.title}
        </h3>
        <div className="flex items-center justify-between text-foreground-400 text-xs">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.readCount >= 10000
              ? `${(article.readCount / 10000).toFixed(1)}万`
              : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}